/**
 * Tickets crew hold (the client layer's field.tickets). Tickets are recorded as held or not, with no
 * expiry dates. A person rostered on an upcoming shift is flagged when they lack a ticket everyone
 * needs, or one the leading hand needs when they lead. An optional recency rule flags people whose
 * last worked or trained month is older than field.recencyMonths.
 */
import { BRAND } from "./config";
import { addDays, localMidnight, localYmd } from "./format";
import { PREVIEW } from "./preview";
import { supabase, type CrewMember } from "./supabase";

type Ticket = NonNullable<typeof BRAND.field>["tickets"][number];
export const TICKETS: Ticket[] = BRAND.field?.tickets ?? [];
export const ticketShort = (t: Ticket) => t.short ?? t.label;

export interface Rostered {
  crew_member_id: string;
  role: string;
  starts_at: string;
}

export interface MissingTickets {
  crewId: string;
  name: string;
  missing: Ticket[];
  /** First upcoming shift they are on. */
  firstShift: string;
  leadingHand: boolean;
}

/** Tickets this person must hold on a shift, given whether they lead it. */
export const requiredTickets = (leadingHand: boolean) => TICKETS.filter((t) => t.requiredFor === "everyone" || (leadingHand && t.requiredFor === "leadingHand"));

/** Rostered crew missing a ticket they need, soonest shift first. */
export function missingTickets(crew: CrewMember[], rostered: Rostered[]): MissingTickets[] {
  if (!TICKETS.length) return [];
  const byId = new Map(crew.map((m) => [m.id, m]));
  const people = new Map<string, { leadingHand: boolean; firstShift: string }>();
  for (const r of rostered) {
    const p = people.get(r.crew_member_id);
    const lead = r.role === "leading_hand";
    if (!p) people.set(r.crew_member_id, { leadingHand: lead, firstShift: r.starts_at });
    else people.set(r.crew_member_id, { leadingHand: p.leadingHand || lead, firstShift: r.starts_at < p.firstShift ? r.starts_at : p.firstShift });
  }
  const out: MissingTickets[] = [];
  for (const [id, p] of people) {
    const m = byId.get(id);
    if (!m) continue;
    const held = new Set(m.tickets_held ?? []);
    const missing = requiredTickets(p.leadingHand).filter((t) => !held.has(t.id));
    if (missing.length) out.push({ crewId: id, name: m.full_name, missing, firstShift: p.firstShift, leadingHand: p.leadingHand });
  }
  return out.sort((a, b) => a.firstShift.localeCompare(b.firstShift));
}

/** Whether the person's last worked or trained month is past the recency limit (false when not set up or unknown). */
export function recencyLapsed(m: Pick<CrewMember, "last_worked_month">, today = localYmd()): boolean {
  const months = BRAND.field?.recencyMonths;
  if (!months || !m.last_worked_month) return false;
  const [y, mo] = today.split("-").map(Number) as [number, number];
  const [ly, lm] = m.last_worked_month.split("-").map(Number) as [number, number];
  return (y - ly) * 12 + (mo - lm) > months;
}

/** Days ahead counted as "rostered" for missing tickets. */
export const ROSTER_DAYS = 14;

/** Crew allocations on shifts from today for ROSTER_DAYS days (not cancelled). */
export async function rosteredCrew(days = ROSTER_DAYS): Promise<Rostered[]> {
  if (PREVIEW || !TICKETS.length) return [];
  const today = localYmd();
  const { data, error } = await supabase
    .from("bookings")
    .select("starts_at, status, allocations(crew_member_id, role)")
    .gte("starts_at", localMidnight(today).toISOString())
    .lt("starts_at", localMidnight(addDays(today, days + 1)).toISOString())
    .neq("status", "cancelled")
    .limit(1000);
  if (error) throw error;
  return (data as { starts_at: string; allocations: { crew_member_id: string; role: string }[] }[]).flatMap((b) =>
    b.allocations.map((a) => ({ crew_member_id: a.crew_member_id, role: a.role, starts_at: b.starts_at })),
  );
}
